<template>
  <div :class="{ 'address-form--required': required }">
    <div v-if="token && !manualMode">
      <dadata
        :label="displayLabel"
        :hint="hint"
        :token="token"
        :variant="variant"
        :debounceMs="debounceMs"
        v-model="value"
        @service-error="onServiceError"
      />
      <div class="d-flex gap-2 mt-2">
        <v-btn
          density="compact"
          variant="text"
          color="blue-darken-4"
          prepend-icon="mdi-home-edit-outline"
          text
          small
          @click="manualMode = true"
        >
          Ввести вручную
        </v-btn>
        <v-alert v-if="serviceErrorMessage" type="error" dense class="flex-grow-1">
          {{ serviceErrorMessage }}
        </v-alert>
      </div>
    </div>

    <div v-if="manualMode">
      <div v-if="label || hint" class="mb-2">
        <div v-if="label" class="text-subtitle-2">
          {{ label }}<span v-if="required" class="text-error font-weight-bold"> *</span>
        </div>
        <div v-if="hint" class="text-caption text-medium-emphasis">{{ hint }}</div>
      </div>

      <v-form autocomplete="off">
        <v-row v-for="(item, levelName) in fiasLevels" :key="levelName">
          <v-col cols="3">
            <v-text-field autocomplete="off"
              :model-value="hintForLevel(levelName)[0]"
              density="compact"
              hide-details
              variant="underlined"
              disabled
            />
          </v-col>
          <v-col cols="3" v-if="showTypes">
            <v-autocomplete autocomplete="off"
              label="Укажите тип"
              filled
              :items="item"
              item-value="fullName"
              item-title="fullName"
              v-model="value.data[levelName.toLowerCase() + '_type_full']"
              variant="underlined"
              density="compact"
              @update:modelValue="onChange($event, levelName)"
            />
          </v-col>
          <v-col>
            <v-text-field autocomplete="off"
              :hint="hintForLevel(levelName)[1] ? 'например: ' + hintForLevel(levelName)[1] : ''"
              v-model="value.data[levelName.toLowerCase()]"
              @update:modelValue="onManualEdit"
              density="compact"
              variant="underlined"
            />
          </v-col>
        </v-row>
      </v-form>
      <div class="mt-3 d-flex gap-2">
        <v-btn
          v-if="token"
          density="compact"
          variant="text"
          color="blue-darken-4"
          prepend-icon="mdi-magnify"
          text
          small
          @click="manualMode = false"
        >
          Вернуться к подсказкам
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import Dadata from "moodleDir/procollege/lib/vue/address/dadata.vue";

let cachedFiasLevels = null;
let cachedFiasPromise = null;

function loadFiasLevels(axios, wwwroot) {
  if (cachedFiasLevels) {
    return Promise.resolve(cachedFiasLevels);
  }
  if (cachedFiasPromise) {
    return cachedFiasPromise;
  }
  cachedFiasPromise = axios
    .post(`${wwwroot}/procollege/index.php/core_procollege/helpers/DaData/getDictionaries`)
    .then((response) => {
      cachedFiasLevels = response.data;
      cachedFiasPromise = null;
      return cachedFiasLevels;
    })
    .catch((error) => {
      cachedFiasPromise = null;
      throw error;
    });
  return cachedFiasPromise;
}

export default {
  emits: ["update:modelValue"],
  props: {
    token: {
      required: true,
      type: [String, Boolean],
    },
    modelValue: {
      required: true,
      type: Object,
    },
    showTypes: {
      required: false,
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "outlined",
    },
    debounceMs: {
      type: Number,
      default: 300,
    },
    label: {
      type: String,
      default: "Адрес",
    },
    hint: {
      type: String,
      default: "Начните заполнять адрес, чтобы получить подсказку",
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      suggestions: [],
      fiasLevels: [],
      manualMode: !this.token,
      serviceErrorMessage: "",
    };
  },
  components: {
    Dadata,
  },
  computed: {
    displayLabel() {
      return this.required && this.label ? `${this.label} *` : this.label;
    },
    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  created() {
    loadFiasLevels(this.axios, this.$model.wwwroot)
      .then((data) => {
        this.fiasLevels = data;
      })
      .catch((error) => {
        console.log(error);
      });
  },
  methods: {
    onChange(val, level) {
      let field = level.toLowerCase();

      if (!this.fiasLevels[level]) {
        return;
      }
      const found = this.fiasLevels[level].find((obj) => obj.fullName === val || obj.name === val || Object.values(obj).includes(val));
      if (found) {
        let tmp = { ...this.value };
        if (!tmp.data) tmp.data = {};
        tmp.data[field + "_type"] = found.name;
        tmp.data[field + "_type_full"] = found.fullName || val;
        tmp.data.fias_id = null;
        tmp.manual_input = true;
        this.$emit("update:modelValue", tmp);
      }
    },
    hintForLevel(val) {
      switch (val) {
        case "REGION":
          return ["Регион", "Челябинская"];
        case "AREA":
          return ["Район в регионе", ""];
        case "CITY":
          return ["Город", "Челябинск"];
        case "SETTLEMENT":
          return ["Населенный пункт", "СНТ Тракторосад"];
        case "STREET":
          return ["Улица", "Ленина"];
        case "HOUSE":
          return ["Дом", "10А"];
        case "APARTMENT":
          return ["Квартира", ""];
        default:
          return ["", ""];
      }
    },
    onServiceError(error) {
      this.serviceErrorMessage = "Сервис подсказок недоступен. Вы можете ввести адрес вручную.";
      console.error("DaData service error:", error);
    },
    onManualEdit() {
      let tmp = { ...this.value };
      if (!tmp.data) tmp.data = {};
      tmp.data.fias_id = null;
      tmp.manual_input = true;
      this.$emit("update:modelValue", tmp);
    },
  },
};
</script>
